export class BalanceType {
  static APPEL_LOYER = "appel_loyer";
  static INDEMNITE_OCCUPATION = "indemnite_occupation";
  static APPEL_DG = "appel_dg";
  static APPEL_CHARGE = "appel_charge";
  static APPEL_COMPLEMENTAIRE = "appel_complementaire";
  static APPEL_CHARGE_EXCEPTIONNELLE = "appel_charge_exceptionnelle";
  static PAIEMENT_DG = "paiement_dg";
  static PAIEMENT_HONORAIRES = "paiement_honoraires";
  static REMBOURSEMENT_DG = "remboursement_dg";
  static PAIEMENT = "paiement";
  static RETENUE_DG = "retenue_dg";
  static RETENUE_DG_LOYERS = "retenue_dg_loyers";
  static COMPLEMENT_DG = "complement_dg";
  static REVERSEMENT_DG_BAILLEUR = "reversement_dg_bailleur";
  static CONSERVATION_DG = "conservation_dg";
  static TRANSFERT_DG = "transfert_dg";
  static REGUL_CHARGES_LOCATAIRE = "regul_charges_locataire";
  static PAIEMENT_BAILLEUR = "paiement_bailleur";
  static FRAIS_GESTION = "frais_gestion";
  static AVOIR_REMISE = "avoir_remise";
}

/** Appel du loyer de la période, ou de l'indemnité d'occupation qui le remplace (occupant sans droit ni titre). */
export const RENT_CALL_TYPES: string[] = [
  BalanceType.APPEL_LOYER,
  BalanceType.INDEMNITE_OCCUPATION,
];

export class BalanceTypeCategory {
  static CHARGE = "charge";
  static TRAVAUX = "travaux";
  static LOCATAIRE_APPEL = "locataire_appel";
  static LOCATAIRE_PAIEMENT = "locataire_paiement";
  static LOCATAIRE_REMBOURSEMENT = "locataire_remboursement";
  static DG_CREDIT = "dg_credit";
  static DG_DEBIT = "dg_debit";
  static DG_TRANSFERT = "dg_transfert";
  static CREDIT = "credit";
  static PAIEMENT_BAILLEUR = "paiement_bailleur";
}

// Clés des familles d'opération du catalogue `/balance-types` (`BalanceTypeFamily` côté back).
export class BalanceTypeFamilyKey {
  static TENANT_CALL = "tenant_call";
}

// Un appel locataire naît du bail, pas d'une transaction bancaire : les écrans qui affectent
// un mouvement de banque (rapprochement, mots-clés, modèles de découpe, import de relevé)
// ne proposent ni les types de catégorie « appel » (avoirs et honoraires locataire compris)
// ni la famille des montants demandés au locataire (dépôt de garantie à payer inclus). Un
// encaissement se range dans « Règlement et régularisation ».
export const BANK_AFFECTATION_TYPE_FILTERS = {
  excludeCategories: [BalanceTypeCategory.LOCATAIRE_APPEL],
  excludeFamilies: [BalanceTypeFamilyKey.TENANT_CALL],
};

// Catégories qui touchent au dépôt de garantie : hors solde locatif (`is_dg` côté back).
export const DG_BALANCE_CATEGORIES = [
  BalanceTypeCategory.DG_CREDIT,
  BalanceTypeCategory.DG_DEBIT,
  BalanceTypeCategory.DG_TRANSFERT,
];

// Appels de dépôt de garantie, soldés par un paiement de dépôt.
export const DG_CALL_TYPES = [BalanceType.APPEL_DG, BalanceType.COMPLEMENT_DG];

// Types des catégories de dépôt de garantie, hors solde locatif.
export const DG_BALANCE_TYPES = [
  ...DG_CALL_TYPES,
  BalanceType.PAIEMENT_DG,
  BalanceType.REMBOURSEMENT_DG,
  BalanceType.REVERSEMENT_DG_BAILLEUR,
  BalanceType.CONSERVATION_DG,
  BalanceType.TRANSFERT_DG,
];

// Sens imposé au montant par le catalogue (`BalanceDirection` côté back).
export const BalanceDirection = {
  DEBIT: "debit",
  CREDIT: "credit",
} as const;

export type BalanceDirectionValue =
  (typeof BalanceDirection)[keyof typeof BalanceDirection];

export const BalanceDomain = {
  TENANT: "tenant",
  PROPERTY: "property",
  FOLDER: "folder",
  PROFILE_BAILLEUR: "profile_bailleur",
} as const;

export type BalanceDomainValue =
  (typeof BalanceDomain)[keyof typeof BalanceDomain];

export const DOMAINE_ID_SEPARATOR = ":";

// Plafond d'affectation des règles automatiques (mot clé, modèle de découpe) : la
// synchronisation bancaire ne sait rattacher qu'un dossier, un bien ou un locataire, jamais
// un profil bailleur.
export const CONTEXTLESS_AFFECTATION_DOMAINS = [
  BalanceDomain.FOLDER,
  BalanceDomain.PROPERTY,
  BalanceDomain.TENANT,
];

export const DOMAIN_TO_PARENT_ID_FIELD_NAME = {
  [BalanceDomain.PROPERTY]: "id_bien",
  [BalanceDomain.TENANT]: "id_tenant",
  [BalanceDomain.FOLDER]: "id_property_folder",
  [BalanceDomain.PROFILE_BAILLEUR]: "id_profile_bailleur",
} as const;

// Racines d'API, pour construire une URL de requête.
export const DOMAIN_TO_ROOT_URL = {
  [BalanceDomain.PROPERTY]: "biens",
  [BalanceDomain.TENANT]: "tenants",
  [BalanceDomain.FOLDER]: "property-folders",
  [BalanceDomain.PROFILE_BAILLEUR]: "profiles",
};

// Routes de l'application, pour construire un lien de navigation — la compta de chaque
// entité vit derrière un paramètre d'onglet.
export const DOMAIN_TO_COMPTA_ROUTE = {
  [BalanceDomain.TENANT]: (id: number | string) =>
    `/bailleur/tenants/${id}?tab-tenant-show=historique-transactions`,
  [BalanceDomain.PROPERTY]: (id: number | string) =>
    `/bailleur/properties/${id}?tab-prop-show=compta`,
  [BalanceDomain.FOLDER]: (id: number | string) =>
    `/bailleur/properties/folder/${id}?tab-prop-show=compta`,
  [BalanceDomain.PROFILE_BAILLEUR]: (id: number | string) =>
    `/bailleur/profiles/${id}?tab-bailleur-show=comptabilite`,
};

// Famille de régime fiscal d'un statut de profil (`StatusFiscal`, models/profile.js) :
// c'est elle que le catalogue `/balance-types` compare (`tax_regimes`). Un statut absent de
// la table (« non », vide) ne filtre rien. Miroir de `StatusFiscal.tax_regime` côté back.
export const TAX_REGIME_BY_STATUS_FISCAL: Record<string, string> = {
  "nu-micro": "revenu_foncier",
  "nu-reel": "revenu_foncier",
  "lmnp-micro": "lmnp",
  "lmnp-reel": "lmnp",
  is: "is",
};

export const TAX_REGIME_LABELS: Record<string, string> = {
  revenu_foncier: "revenus fonciers",
  lmnp: "LMNP / LMP",
  is: "société à l'IS",
};

// Tolérance d'arrondi entre des sous-montants saisis au centime et le montant
// (`_DETAILS_TOLERANCE` côté back).
export const DETAILS_TOLERANCE = 0.005;

// Sous-montants « Dont … » que le catalogue peut ouvrir (`BalanceDetails` côté back).
export const BALANCE_DETAIL_AMOUNT_KEYS = [
  "principal",
  "interest",
  "insurance",
  "waste_tax",
  "owner_share",
  "tenant_share",
  "works_fund",
] as const;

export const ENERGY_OPTIONS = [
  { value: "water", label: "Eau" },
  { value: "electricity", label: "Électricité" },
  { value: "heating", label: "Chauffage" },
  { value: "gas", label: "Gaz" },
  { value: "other", label: "Autre" },
];
